import React, { useEffect, useRef, useState } from 'react';
import { Camera, StopCircle, Loader2, Flashlight, FlashlightOff, Keyboard, RefreshCw as Refresh, History as HistoryIcon } from 'lucide-react';
// Import Quagga directly without type checking
import Quagga from 'quagga';
import { successSound } from '../assets/success-sound';
import { useStore, get } from '../store';
import { supabase, logError } from '../lib/supabase';
import { translations } from '../types';
import { Play, Check, X } from 'lucide-react';

export interface ScannerProps {
  onScan?: (code: string) => void;
  onShowHistory?: () => void;
}

export function Scanner({ onScan, onShowHistory }: ScannerProps) {
  const scannerContainerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchCapabilities, setTorchCapabilities] = useState<MediaTrackCapabilities | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [scanMessage, setScanMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cooldownActive, setCooldownActive] = useState(false);
  const lastScannedRef = useRef<{ code: string; timestamp: number }>({ code: '', timestamp: 0 });
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isAutoPaused, setIsAutoPaused] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const { language, addPendingScan, pendingScans, confirmPendingScan, removePendingScan } = useStore();
  const t = translations[language];
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentScan, setCurrentScan] = useState<ScanResult | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  
  const processTrackingNumber = (rawValue: string | null | undefined): string => {
    if (!rawValue) {
      logError({
        error_message: 'Пустой трек-номер',
        error_type: 'VALIDATION'
      });
      throw new Error(t.invalidTrackingNumber);
    }

    // Remove whitespace, convert to lowercase, and remove -X-X or -X-X- pattern
    const cleanValue = rawValue
      .replace(/\s+/g, '')
      .toLowerCase()
      .replace(/-\d-\d-?$/, '');
    
    if (!cleanValue) {
      logError({
        error_message: 'Пустой трек-номер после очистки',
        error_type: 'VALIDATION'
      });
      throw new Error(t.invalidTrackingNumber);
    }

    return cleanValue;
  };

  const checkDuplicate = async (trackingNumber: string): Promise<boolean> => {
    try {
      const cleanNumber = trackingNumber.trim().toLowerCase();
      
      // Check in scans table
      const { data: scansData, error: scansError } = await supabase
        .from('scans')
        .select('tracking_numbers')
        .ilike('tracking_numbers', cleanNumber)
        .limit(1);

      if (scansError) {
        console.error('Error checking for duplicate in scans:', scansError);
        return false;
      }

      return scansData && scansData.length > 0;
    } catch (error) {
      console.error('Error in checkDuplicate:', error);
      return false;
    }
  };

  const handleDuplicate = (trackingNumber: string, format: string) => {
    // Show duplicate dialog
    setShowDuplicateDialog(true);
    setShowConfirmation(false); // Ensure confirmation dialog is closed
    setCurrentScan({
      tracking_numbers: trackingNumber,
      timestamp: Date.now(),
      format: format
    });
    
    // Pause scanner if running
    if (isScanning) {
      Quagga.pause();
      setIsPaused(true);
    }
    
    // Play warning sound
    playSuccessSound();
    
    setIsDuplicate(true);
    setTimeout(() => setIsDuplicate(false), 2000);
  };

  const saveScanToDatabase = async (scan: { 
    tracking_numbers: string;
    timestamp: string;
    format: string;
    project_id?: string;
  }) => {
    try {
      console.log('Attempting to save scan:', scan);
      const cleanNumber = scan.tracking_numbers.toLowerCase().trim();
      
      // First check for duplicate before attempting to save
      const isDuplicate = await checkDuplicate(cleanNumber);
      
      if (isDuplicate) {
        // Handle as duplicate - show dialog
        handleDuplicate(cleanNumber, scan.format);
        return;
      }
      
      // Prepare data
      const cleanScan = {
        tracking_numbers: cleanNumber,
        timestamp: scan.timestamp,
        format: scan.format,
        project_id: scan.project_id || 'scanner'
      };
      
      console.log('Saving new scan to scans table:', cleanScan);
      
      // Insert directly into scans table
      const { data, error } = await supabase
        .from('scans')
        .insert(cleanScan);
      
      if (error) {
        console.error('Error saving to scans table:', error);
        
        // If it's a duplicate constraint violation
        if (error.code === '23505') {
          handleDuplicate(cleanNumber, scan.format);
          return;
        }
        throw new Error('Ошибка при сохранении');
      }

      console.log('Scan saved successfully');
      return true;
    } catch (error) {
      console.error('Database error:', error);
      logError({
        error_message: error instanceof Error ? error.message : 'Unknown database error',
        error_details: {
          tracking_numbers: scan.tracking_numbers,
          error_type: 'DATABASE',
          stack_trace: error instanceof Error ? error.stack : undefined
        }
      });
      throw error;
    }
  };

  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.src = successSound;
    audioRef.current.volume = 1.0;
    audioRef.current.preload = 'auto';
    
    // Clean up scanner when component unmounts
    return () => {
      if (isScanning) {
        stopScanning();
      }
    };
  }, [isScanning]);

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setScanMessage({ type, text });
    setTimeout(() => setScanMessage(null), 3000);
  };

  const toggleTorch = async () => {
    try {
      if (!videoTrackRef.current || !torchCapabilities?.torch) {
        throw new Error('Torch not supported on this device');
      }

      const newTorchState = !torchOn;
      await videoTrackRef.current.applyConstraints({
        advanced: [{ torch: newTorchState }]
      });
      
      setTorchOn(newTorchState);
      showMessage('info', newTorchState ? t.torchEnabled : t.torchDisabled);
    } catch (err) {
      logError({
        error_message: 'Torch control error',
        error_details: {
          error_type: 'CAMERA',
          stack_trace: err instanceof Error ? err.stack : String(err)
        }
      });
      console.error('Torch error:', err);
      setError(t.torchError);
      setTorchOn(false);
    }
  };

  const playSuccessSound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        logError({
          error_message: 'Audio playback error',
          error_details: {
            error_type: 'SCAN',
            stack_trace: err instanceof Error ? err.stack : String(err)
          }
        });
        console.error('Audio playback error:', err);
      });
    }
  };

  const showSuccessFlash = () => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 600);
  };

  const restartScanner = async () => {
    if (isScanning) {
      stopScanning();
      setTimeout(() => {
        startScanning();
      }, 500);
    }
  };

  const handleSuccessfulScan = async (result: any) => {
    console.log('Barcode detected:', result);
    const now = Date.now();
    const decodedText = result.codeResult.code;

    // Prevent processing if already handling a scan or scanner is paused
    if (isProcessing || isPaused) {
      return;
    }

    setIsProcessing(true);
    
    // Pause scanner immediately after detecting barcode
    if (isScanning) {
      Quagga.pause();
      setIsPaused(true);
    }

    try {
      console.log('Raw scan result:', decodedText);
      const cleanTrackingNumber = processTrackingNumber(decodedText);
      
      // Check for duplicate in database first
      const isDuplicate = await checkDuplicate(cleanTrackingNumber);
      
      if (isDuplicate) {
        handleDuplicate(cleanTrackingNumber, result.codeResult.format);
        setIsProcessing(false);
        return;
      }
      
      // Create scan object only if not duplicate
      const newScan = {
        tracking_numbers: cleanTrackingNumber,
        timestamp: now,
        format: result.codeResult.format
      };

      // Show confirmation dialog
      setCurrentScan(newScan);
      setShowConfirmation(true);

      // Play success sound and show flash
      playSuccessSound();
      showSuccessFlash();
      showMessage('success', t.scanSuccess);
      
      // If onScan callback is provided, call it
      if (onScan) {
        onScan(cleanTrackingNumber);
        return;
      }
      
      setIsProcessing(false);
    } catch (error) {
      console.error('Error in saveScanWithFallback:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Непредвиденная ошибка'
      };
    }
  };

  const resumeScanning = () => {
    if (isPaused) {
      Quagga.start();
      setIsPaused(false);
      setCooldownActive(false);
      lastScannedRef.current = { code: '', timestamp: 0 };
      showMessage('info', t.scanInstructions);
    }
  };

  const startScanning = async () => {
    try {
      setIsInitializing(true);
      setIsScanning(false);
      setIsProcessing(false);
      setCooldownActive(false);
      lastScannedRef.current = { code: '', timestamp: 0 };
      setError(null);
      
      if (!scannerContainerRef.current) {
        console.error('Scanner container not found');
        setError('Scanner initialization failed');
        setIsInitializing(false);
        return;
      }

      // Request camera permission first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: "environment",
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 }
          }
        });
        // Stop any existing streams
        stream.getTracks().forEach(track => track.stop());
        console.log('Camera permission granted');
        
        // Get video track and check torch capabilities
        const videoTrack = stream.getVideoTracks()[0];
        const capabilities = videoTrack.getCapabilities();
        
        // Store video track and capabilities
        videoTrackRef.current = videoTrack;
        setTorchCapabilities(capabilities);
        
        console.log('Torch capabilities:', capabilities?.torch ? 'Supported' : 'Not supported');
      } catch (err) {
        console.error('Camera permission error:', err);
        setError(t.cameraError);
        setIsInitializing(false);
        return;
      }

      // Initialize Quagga with simplified configuration for reliability
      Quagga.init({
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: scannerContainerRef.current,
          constraints: {
            width: 1280,
            height: 720,
            facingMode: "environment"
          },
        },
        locator: {
          patchSize: "medium",
          halfSample: true
        },
        numOfWorkers: 2,
        frequency: 10,
        decoder: {
          readers: [
            "code_128_reader", // Code 128
            "ean_reader",      // EAN-13
            "upc_reader"       // UPC
          ],
          multiple: false
        },
        locate: true
      }, function(err) {
        if (err) {
          console.error('Quagga initialization error:', err);
          setError(t.cameraError);
          setIsInitializing(false);
          setIsScanning(false);
          return;
        }
        
        console.log('Quagga initialized successfully, starting scanner...');
        
        // Start Quagga
        try {
          Quagga.start();
          setIsScanning(true);
          setIsInitializing(false);
        } catch (startError) {
          console.error('Quagga start error:', startError);
          setError(t.cameraError);
          setIsInitializing(false);
          setIsScanning(false);
          return;
        }
        
        // Register debug canvas
        const canvases = document.querySelectorAll('canvas.drawingBuffer');
        for (let i = 0; i < canvases.length; i++) {
          const canvas = canvases[i] as HTMLCanvasElement;
          canvas.style.position = 'absolute';
          canvas.style.top = '0';
          canvas.style.left = '0';
          canvas.style.width = '100%';
          canvas.style.height = '100%';
        }

        console.log('Setting up barcode detection event handlers...');
        
        // Set up event listeners
        Quagga.onDetected(handleSuccessfulScan);
        
        // For debugging
        Quagga.onProcessed(function(result) {
          if (!result) return;
          
          const drawingCtx = Quagga.canvas.ctx.overlay;
          const drawingCanvas = Quagga.canvas.dom.overlay;
          
          if (!drawingCtx || !drawingCanvas) return;

          // Clear previous drawings
          drawingCtx.clearRect(
            0, 0, 
            parseInt(drawingCanvas.getAttribute("width") || '0'), 
            parseInt(drawingCanvas.getAttribute("height") || '0')
          );

          // Draw detected barcodes
          if (result.boxes) {
            drawingCtx.strokeStyle = 'green';
            drawingCtx.lineWidth = 2;
            
            result.boxes.filter(function(box) {
              return box !== result.box;
            }).forEach(function(box) {
              drawingCtx.beginPath();
              drawingCtx.moveTo(box[0][0], box[0][1]);
              drawingCtx.lineTo(box[1][0], box[1][1]);
              drawingCtx.lineTo(box[2][0], box[2][1]);
              drawingCtx.lineTo(box[3][0], box[3][1]);
              drawingCtx.lineTo(box[0][0], box[0][1]);
              drawingCtx.stroke();
            });
          }

          // Highlight the main detected barcode
          if (result.box) {
            drawingCtx.strokeStyle = 'red';
            drawingCtx.lineWidth = 4;
            
            drawingCtx.beginPath();
            drawingCtx.moveTo(result.box[0][0], result.box[0][1]);
            drawingCtx.lineTo(result.box[1][0], result.box[1][1]);
            drawingCtx.lineTo(result.box[2][0], result.box[2][1]);
            drawingCtx.lineTo(result.box[3][0], result.box[3][1]);
            drawingCtx.lineTo(result.box[0][0], result.box[0][1]);
            drawingCtx.stroke();
          }

          // Draw the barcode line
          if (result.codeResult && result.codeResult.code) {
            drawingCtx.strokeStyle = 'blue';
            drawingCtx.lineWidth = 5;
            drawingCtx.beginPath();
            
            const line = result.line;
            if (line && line.length >= 2) {
              drawingCtx.moveTo(line[0].x, line[0].y);
              drawingCtx.lineTo(line[1].x, line[1].y);
              drawingCtx.stroke();
            }
          }
        });
      });
    } catch (error) {
      logError({
        error_message: 'Camera initialization error',
        error_details: {
          error_type: 'CAMERA',
          stack_trace: error instanceof Error ? error.stack : String(error)
        }
      });
      console.error('Camera error:', error);
      setError(t.cameraError);
      setIsInitializing(false);
    }
  };

  const stopScanning = () => {
    try {
      if (isScanning) {
        // Turn off torch if it's on
        if (torchOn && videoTrackRef.current) {
          videoTrackRef.current.applyConstraints({
            advanced: [{ torch: false }]
          }).catch(console.error);
          setTorchOn(false);
        }
        
        console.log('Stopping Quagga scanner...');
        Quagga.stop();
        setIsScanning(false);
        setIsProcessing(false);
        lastScannedRef.current = { code: '', timestamp: 0 };
        setCooldownActive(false);
        setError(null);
      }
    } catch (err) {
      logError({
        error_message: 'Scanner stop error',
        error_details: {
          error_type: 'CAMERA',
          stack_trace: err instanceof Error ? err.stack : String(err)
        }
      });
      console.error('Stop error:', err);
      setError(t.stopError);
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    if (manualCode) {
      try {
        const cleanTrackingNumber = processTrackingNumber(manualCode);
        
        // Check for duplicate first
        const isDuplicate = await checkDuplicate(cleanTrackingNumber);
        
        if (isDuplicate) {
          handleDuplicate(cleanTrackingNumber, 'MANUAL');
          
          setManualCode('');
          setShowManualInput(false);
          setIsProcessing(false);
          return;
        }
        
        // Create scan object for non-duplicates
        const newScan = {
          tracking_numbers: cleanTrackingNumber,
          timestamp: Date.now(),
          format: 'MANUAL'
        };

        // Show confirmation dialog
        setCurrentScan(newScan);
        setShowConfirmation(true);

        setManualCode('');
        setShowManualInput(false);
        playSuccessSound();
        showSuccessFlash();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t.scanError;
        
        logError({
          error_message: errorMessage,
          error_details: {
            tracking_numbers: manualCode,
            error_type: 'SCAN',
            stack_trace: error instanceof Error ? error.stack : undefined
          }
        });
        showMessage('error', errorMessage);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleDuplicateDialogClose = () => {
    setShowDuplicateDialog(false);
    setCurrentScan(null);
    setIsProcessing(false); 
  };

  return (
    <div className="relative w-full space-y-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mx-4 z-10">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      <div className="relative bg-black overflow-hidden h-[60vh] z-0" ref={scannerContainerRef}>
        {isScanning && (
          <>
            <div className="absolute inset-0 pointer-events-none">
              {showSuccess && (
                <div className="absolute inset-0 bg-green-500 bg-opacity-50 success-flash" />
              )}
              {isDuplicate && (
                <div className="absolute inset-0 bg-yellow-500 bg-opacity-50" />
              )}
              {scanMessage && (
                <div className={`absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg shadow-lg ${
                  scanMessage.type === 'success' ? 'bg-green-500' : 
                  scanMessage.type === 'error' ? 'bg-yellow-500' : 'bg-blue-500'
                } text-white`}>
                  {scanMessage.text}
                </div>
              )}
              {cooldownActive && !scanMessage && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg shadow-lg bg-blue-500 text-white">
                  {t.cooldown}
                </div>
              )}
              
              {/* Enhanced scanning guide overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-64 border-2 border-white rounded-lg opacity-60"></div>
                <div className="scanner-guide"></div>
              </div>
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-sm">
              {t.scanInstructions}
            </div>
            
            {/* Camera controls */}
            <div className="absolute top-4 right-4 flex flex-col gap-2">
              <button
                onClick={toggleTorch}
                className="bg-black bg-opacity-60 text-white p-3 rounded-full hover:bg-opacity-80 transition-colors"
                title={torchOn ? "Turn off flash" : "Turn on flash"}
              >
                {torchOn ? (
                  <FlashlightOff className="w-5 h-5" />
                ) : (
                  <Flashlight className="w-5 h-5" />
                )}
              </button>
              
              <button
                onClick={restartScanner}
                className="bg-black bg-opacity-60 text-white p-3 rounded-full hover:bg-opacity-80 transition-colors"
                title="Restart scanner"
              >
                <Refresh className="w-5 h-5" />
              </button>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-center gap-2 px-4 relative z-10">
        <div className="w-full max-w-md grid grid-cols-3 gap-2">
          {!isScanning ? (
            <>
              <button
                onClick={startScanning}
                disabled={isInitializing || isProcessing}
                className={`
                  col-span-2 h-10 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-colors text-sm
                  ${isInitializing || isProcessing
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'}
                  text-white
                `}
              >
                {isInitializing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.initializing}
                  </>
                ) : (
                  <>
                    <Camera className="w-5 h-5" />
                    {t.scan}
                  </>
                )}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowManualInput(true)}
                  disabled={isInitializing || isProcessing}
                  className="flex-1 h-10 bg-gray-600 text-white px-2 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors text-sm"
                >
                  <Keyboard className="w-5 h-5" />
                </button>
                <button
                  onClick={onShowHistory}
                  disabled={isInitializing || isProcessing}
                  className="flex-1 h-10 bg-red-600 text-white px-2 rounded-lg flex items-center justify-center hover:bg-red-700 transition-colors text-sm"
                >
                  <HistoryIcon className="w-5 h-5" />
                </button>
              </div>
            </>
          ) : (
            <>
              {isPaused ? (
                <button
                  onClick={resumeScanning}
                  disabled={isProcessing}
                  className="col-span-2 h-10 bg-green-600 text-white px-3 rounded-lg flex items-center justify-center gap-1.5 hover:bg-green-700 transition-colors text-sm"
                >
                  <Play className="w-5 h-5" />
                  {t.resume}
                </button>
              ) : (
                <button
                  onClick={stopScanning}
                  disabled={isProcessing}
                  className="col-span-2 h-10 bg-red-600 text-white px-3 rounded-lg flex items-center justify-center gap-1.5 hover:bg-red-700 transition-colors text-sm"
                >
                  <StopCircle className="w-5 h-5" />
                  {t.stop}
                </button>
              )}
              <button
                onClick={() => setShowManualInput(true)}
                disabled={isProcessing}
                className="h-10 bg-gray-600 text-white px-2 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors text-sm"
              >
                <Keyboard className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {showConfirmation && currentScan && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 pointer-events-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t.confirmScan}</h3>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="font-mono text-lg mb-2">{currentScan.tracking_numbers}</div>
              <div className="text-sm text-gray-500">
                {t.format}: {currentScan.format}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  // Just close the dialog, keep scanner paused
                  setShowConfirmation(false);
                  setCurrentScan(null);
                  setIsProcessing(false);
                }}
                className="px-4 py-2 flex items-center gap-2 text-red-600 hover:bg-red-50 rounded-lg"
                disabled={isProcessing}
              >
                <X className="w-5 h-5" />
                {t.cancel}
              </button>
              <button
                onClick={async () => {
                  setIsProcessing(true);
                  const isDuplicate = await checkDuplicate(currentScan.tracking_numbers);
                  if (isDuplicate) {
                    handleDuplicate(currentScan.tracking_numbers, currentScan.format);
                    setShowConfirmation(false);
                    setCurrentScan(null);
                    return;
                  }
                  addPendingScan(currentScan);
                  const result = await confirmPendingScan(currentScan.timestamp);
                  if (result.success) {
                    showMessage('success', t.trackingSaved);
                  } else {
                    showMessage('error', result.message);
                  }
                  setShowConfirmation(false);
                  setCurrentScan(null);
                  setIsProcessing(false); 
                }}
                className={`px-4 py-2 flex items-center gap-2 ${
                  isProcessing 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                } text-white rounded-lg`}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.saving}
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    {t.confirm}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualInput && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t.manualInput}</h3>
            <form onSubmit={handleManualSubmit}>
              <input
                type="text"
                value={manualCode}
                onChange={(e) => {
                  // Only allow Latin letters and numbers
                  const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
                  setManualCode(value);
                }}
                className="w-full px-4 py-2 border rounded-lg mb-4 font-mono"
                placeholder={t.enterBarcode}
                autoFocus
                pattern="[a-zA-Z0-9]*"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowManualInput(false);
                    setManualCode('');
                  }}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
                >
                  <X className="w-5 h-5" />
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={!manualCode.trim() || isProcessing}
                  className={`px-4 py-2 flex items-center gap-2 ${
                    !manualCode.trim() || isProcessing
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white rounded-lg`}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t.saving}
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      {t.submit}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDuplicateDialog && currentScan && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 pointer-events-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t.duplicateTitle}</h3>
            <div className="bg-yellow-50 p-4 rounded-lg mb-4">
              <div className="font-mono text-lg mb-2">{currentScan.tracking_numbers}</div>
              <div className="text-sm text-yellow-700">
                {t.duplicateMessage}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => handleDuplicateDialogClose()}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <Check className="w-5 h-5" />
                {t.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}